/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { css, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { token } from '@atlaskit/tokens';

import { renderChevronLeftIcon, renderChevronRightIcon, renderFullscreenEnterIcon } from './icons';
import { messages } from './messages';

// [CCI-17296] Inline step-through navigation cluster — previous / "N / N"
// counter / next — shown to the left of the action icons when the user is
// stepping through more than one change.
const navigationTrayCSS = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.050'),
});

const segmentCounterCSS = css({
	font: token('font.body.small'),
	color: token('color.text.subtle'),
	whiteSpace: 'nowrap',
	textAlign: 'center',
	minWidth: token('space.300'),
});

const actionButtonsCSS = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.050'),
});

export interface ActionButtonsProps {
	canGoNext: boolean;
	canGoPrevious: boolean;
	isStepThroughExpanded: boolean;
	onNext: () => void;
	onPrevious: () => void;
	onToggleStepThrough: () => void;
	segmentIndex: number;
	totalSegments: number;
}

/**
 * The "Step through changes" cluster for the Post Stream Review toolbar.
 *
 * Renders a single "Step through changes" toggle button. When expanded it
 * reveals previous/next navigation arrows and a "current / total" counter.
 * When there is only a single segment nothing actionable is rendered.
 */
export function ActionButtons({
	totalSegments,
	segmentIndex,
	canGoPrevious,
	canGoNext,
	isStepThroughExpanded,
	onPrevious,
	onNext,
	onToggleStepThrough,
}: ActionButtonsProps): JSX.Element | null {
	const intl = useIntl();

	// [CCI-17296] Step-through is only relevant when there is more than one
	// reviewable change.
	const hasMultipleSegments = totalSegments > 1;
	// [CCI-17297] Arrows are always enabled when there is more than one segment;
	// navigation wraps around (first <- last, last -> first). Do NOT additionally
	// disable at the ends — `canGoPrevious` / `canGoNext` already encode the
	// wrap-around availability.
	const isPrevDisabled = !canGoPrevious;
	const isNextDisabled = !canGoNext;

	if (!hasMultipleSegments) {
		return null;
	}

	return (
		<div css={actionButtonsCSS}>
			{isStepThroughExpanded && (
				<div css={navigationTrayCSS} data-testid="post-stream-review-navigation">
					<IconButton
						appearance="subtle"
						icon={renderChevronLeftIcon}
						label={intl.formatMessage(messages.previousChange)}
						isDisabled={isPrevDisabled}
						onClick={onPrevious}
						testId="post-stream-review-prev"
					/>
					<span css={segmentCounterCSS} data-testid="post-stream-review-segment-counter">
						{intl.formatMessage(messages.segmentPosition, {
							current: segmentIndex + 1,
							total: totalSegments,
						})}
					</span>
					<IconButton
						appearance="subtle"
						icon={renderChevronRightIcon}
						label={intl.formatMessage(messages.nextChange)}
						isDisabled={isNextDisabled}
						onClick={onNext}
						testId="post-stream-review-next"
					/>
				</div>
			)}
			<IconButton
				appearance="subtle"
				icon={renderFullscreenEnterIcon}
				label={intl.formatMessage(messages.stepThrough)}
				isSelected={isStepThroughExpanded}
				aria-expanded={isStepThroughExpanded}
				onClick={onToggleStepThrough}
				testId="post-stream-review-step-through"
			/>
		</div>
	);
}
